// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {Form, ResponsiveType, useModal, usePopover, useState} from '@supermove/hooks';
import {EmailTemplate, EmailTemplateModel, ProjectModel} from '@supermove/models';
import {colors} from '@supermove/styles';
import {openNewTab} from '@supermove/utils';

// App
import IconButton from '@shared/design/components/IconButton';
import {WrappedEmailFormType} from '@shared/modules/Email/hooks/useSendEmailForProjectMutation';
import CommunicationInboxFooterEmailTemplatesPopover from 'modules/Communication/CommunicationInbox/ProjectConversation/components/footer/CommunicationInboxFooterEmailTemplatesPopover';
import DiscardEmailModal from 'modules/Communication/CommunicationInbox/ProjectConversation/components/footer/DiscardEmailModal';
import AttachmentDetailsModal from 'modules/Communication/Email/components/AttachmentDetailsModal';
import FileTag from 'modules/Communication/Email/components/FileTag';
import EmailAttachmentDrawer from 'modules/File/Attachment/components/EmailAttachmentDrawer';

const Container = Styled.View`
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const iconSize = 14;

const EmailActionButtons = ({
  form,
  project,
  field,
  responsive,
  refetch,
  submitting,
  selectedEmailTemplateId,
  replyThreadUuid,
  handleSelectEmailTemplate,
  handleDiscardEmail,
}: {
  form: Form<WrappedEmailFormType>;
  project: ProjectModel;
  field: string;
  responsive: ResponsiveType;
  refetch: () => void;
  submitting: boolean;
  selectedEmailTemplateId?: string;
  replyThreadUuid?: string;
  handleSelectEmailTemplate: (emailTemplate: EmailTemplateModel) => void;
  handleDiscardEmail: () => void;
}) => {
  const attachmentsFieldName = field ? `${field}.attachmentIds` : 'attachmentIds';
  const [addAttachmentsDrawerOpen, setAddAttachmentsDrawerOpen] = useState(false);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string>();
  const projectAttachments = project.filteredAttachments;
  const libraryAttachments = project.organization.emailTemplateAttachments;
  const allAttachments = _.union(projectAttachments, libraryAttachments);
  const attachedAttachmentIds = _.get(form.values, attachmentsFieldName);
  const attachedAttachments = allAttachments.filter((attachment) =>
    attachedAttachmentIds.includes(attachment.id),
  );
  const popover = usePopover({name: 'Communication Inbox FooterEmail Templates'});
  const emailTemplates = EmailTemplate.getComposeEmailTemplates({
    form,
    field,
    organization: project.organization,
  });
  const discardEmailModal = useModal({name: 'Discard Email Modal'});
  const hasChanges = form.values.emailForm.body || form.values.emailForm.subject;

  const handleOpenEmail = () => {
    const isReply = !!form.values.emailForm.threadId;
    const encodedEmailForm = encodeURIComponent(
      // KeepsFieldsOnce ensures the body and subject are not overwritten by the email template
      JSON.stringify({...form.values.emailForm, keepFieldsOnce: true}),
    );

    openNewTab(
      `/projects/${project.uuid}/${isReply ? `emails/${replyThreadUuid}` : 'email/compose'}?initialEmailForm=${encodedEmailForm}`,
    );
  };

  return (
    <Container>
      <Row>
        {attachedAttachments.map((attachment) => (
          <React.Fragment key={attachment.id}>
            <FileTag
              file={attachment.file}
              onPress={() => setSelectedAttachmentId(attachment.id)}
            />
            <Space width={4} />
          </React.Fragment>
        ))}
      </Row>
      <Space height={4} />
      <Row>
        <CommunicationInboxFooterEmailTemplatesPopover
          popover={popover}
          selectedEmailTemplateId={selectedEmailTemplateId}
          emailTemplates={emailTemplates}
          form={form}
          field={field}
          handleSelectEmailTemplateUuid={(emailTemplateUuid) => {
            const emailTemplate = emailTemplates.find(({uuid}) => uuid === emailTemplateUuid);
            if (!emailTemplate) {
              return;
            }
            handleSelectEmailTemplate(emailTemplate);
          }}
        >
          <IconButton
            isDisabled={submitting}
            source={Icon.FilePen}
            color={colors.gray.secondary}
            size={iconSize}
            onPress={popover.handleToggle}
            tooltip={'Select template'}
            tooltipPlacement={'top'}
          />
        </CommunicationInboxFooterEmailTemplatesPopover>
        <Space width={8} />
        <IconButton
          source={Icon.Paperclip}
          color={colors.gray.secondary}
          size={iconSize}
          onPress={() => setAddAttachmentsDrawerOpen(true)}
          isDisabled={submitting}
          tooltip={'Attach files'}
          tooltipPlacement={'top'}
        />
        <Space width={8} />
        <IconButton
          source={Icon.ExternalLinkAlt}
          color={colors.gray.secondary}
          size={iconSize}
          onPress={handleOpenEmail}
          isDisabled={submitting}
          tooltip={'Open in new tab'}
          tooltipPlacement={'top'}
        />
        <Space width={8} />
        <IconButton
          source={Icon.Trash}
          color={colors.red.warning}
          size={iconSize}
          isDisabled={submitting}
          onPress={hasChanges ? discardEmailModal.handleToggle : handleDiscardEmail}
          tooltip={'Discard'}
          tooltipPlacement={'top'}
        />
        <EmailAttachmentDrawer
          isOpen={addAttachmentsDrawerOpen}
          handleClose={() => setAddAttachmentsDrawerOpen(false)}
          projectUuid={project.uuid}
          form={form}
          attachmentsField={attachmentsFieldName}
          attachedAttachments={attachedAttachments}
          projectAttachments={projectAttachments}
          libraryAttachments={libraryAttachments}
          responsive={responsive}
          refetch={refetch}
        />
      </Row>
      <DiscardEmailModal
        isOpen={discardEmailModal.isOpen}
        handleClose={discardEmailModal.handleClose}
        handleDiscardEmail={handleDiscardEmail}
      />
      <AttachmentDetailsModal
        attachments={attachedAttachments}
        responsive={responsive}
        selectedAttachmentId={selectedAttachmentId}
        setSelectedAttachmentId={setSelectedAttachmentId}
      />
    </Container>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------

EmailActionButtons.fragment = gql`
  ${EmailAttachmentDrawer.fragment}
  ${FileTag.fragment}
  ${CommunicationInboxFooterEmailTemplatesPopover.fragment}
  ${EmailTemplate.getComposeEmailTemplates.fragment}
  ${AttachmentDetailsModal.fragment}

  fragment EmailActionButtons_Project on Project {
    id
    uuid
    filteredAttachments {
      id
      file {
        id
        ...FileTag
        ...EmailAttachmentDrawer_File
        ...AttachmentDetailsModal
      }
    }
    organization {
      id
      emailTemplateAttachments {
        id
        file {
          id
          ...FileTag
          ...EmailAttachmentDrawer_File
        }
      }
      allEmailTemplatesSorted {
        id
        uuid
        ...CommunicationInboxFooterEmailTemplatesPopover
      }
      ...EmailTemplate_getComposeEmailTemplates_Organization
    }
  }
`;

export default EmailActionButtons;
